import { useState } from 'react';

import { Download as DownloadIcon } from '@mui/icons-material';
import { Button, CircularProgress, Menu, MenuItem } from '@mui/material';

import { downloadExport, type ExportFormat } from '@/lib/api/export';

interface Props {
  /** 다운로드할 백엔드 URL. 예: '/export/demo' */
  baseUrl: string;
  /** 다운로드 실패 시 대체 파일명 (확장자 제외) */
  fallbackBaseName?: string;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 버튼 라벨 (기본: 내보내기) */
  label?: string;
  /** baseUrl/${format} 뒤에 붙는 쿼리 파라미터 (from/to 등) */
  params?: Record<string, string | number | undefined>;
}

const OPTIONS: { format: ExportFormat; label: string }[] = [
  { format: 'xlsx', label: 'Excel (.xlsx)' },
  { format: 'csv', label: 'CSV (.csv)' },
  { format: 'pdf', label: 'PDF (.pdf)' },
];

/**
 * 의뢰서 4-5 데이터 내보내기 공통 버튼.
 * Excel / CSV / PDF 3가지 포맷 드롭다운.
 */
export function ExportMenu({
  baseUrl,
  fallbackBaseName = 'export',
  disabled,
  label = '내보내기',
  params,
}: Props) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const buildUrl = (format: ExportFormat): string => {
    const base = `${baseUrl}/${format}`;
    if (!params) return base;
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    const q = qs.toString();
    return q ? `${base}?${q}` : base;
  };

  const handleSelect = async (format: ExportFormat): Promise<void> => {
    setAnchor(null);
    setBusy(format);
    try {
      await downloadExport(buildUrl(format), `${fallbackBaseName}.${format}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        disabled={disabled || busy !== null}
        startIcon={busy ? <CircularProgress size={16} /> : <DownloadIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        data-testid="export-menu-trigger"
      >
        {label}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {OPTIONS.map((opt) => (
          <MenuItem
            key={opt.format}
            onClick={() => handleSelect(opt.format)}
            data-testid={`export-menu-${opt.format}`}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
